import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Phone, Package, Plus, Search } from "lucide-react";

const Trade = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [variety, setVariety] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (city) params.city = city;
    if (variety) params.variety = variety;
    api
      .get("/listings", { params })
      .then((r) => setListings(r.data.listings))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const cities = useMemo(() => {
    const s = new Set(listings.map((l) => l.seller_city).filter(Boolean));
    return Array.from(s);
  }, [listings]);

  return (
    <div data-testid="trade-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">{t.trade.title}</h1>
          <p className="text-stone-600 mt-1">{t.trade.subtitle}</p>
        </div>
        <Link
          to={user ? "/listings/new" : "/login"}
          data-testid="trade-sell-cta"
          className="btn-secondary !h-11"
        >
          <Plus size={18} /> {t.trade.sell_cta}
        </Link>
      </div>

      {/* Filters */}
      <div className="card-gc p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <select
          data-testid="trade-city-filter"
          className="input-gc"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        >
          <option value="">{t.trade.all_cities}</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          data-testid="trade-variety-filter"
          className="input-gc"
          value={variety}
          onChange={(e) => setVariety(e.target.value)}
        >
          <option value="">{t.trade.all_varieties}</option>
          <option value="Desi">Desi</option>
          <option value="G2">G2</option>
          <option value="Amleta">Amleta</option>
        </select>
        <button data-testid="trade-search-btn" onClick={load} className="btn-primary">
          <Search size={18} /> {t.trade.search}
        </button>
      </div>

      {loading ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.common.loading}</div>
      ) : listings.length === 0 ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.trade.no_listings}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="listings-grid">
          {listings.map((l, idx) => (
            <article
              key={l.id}
              data-testid={`listing-card-${idx}`}
              className="card-gc overflow-hidden flex flex-col hover:shadow-md transition-shadow"
            >
              <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                {l.image_url ? (
                  <img src={l.image_url} alt={l.variety} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300">
                    <Package size={48} />
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F4F1EA] text-[#1E4D2B]">
                    {l.variety}
                  </span>
                  <span className="text-2xl font-bold text-[#1E4D2B]">
                    ₹{l.price_per_kg}<span className="text-xs text-stone-500 font-normal">/kg</span>
                  </span>
                </div>
                <h3 className="font-semibold text-stone-900">{l.seller_name || "Seller"}</h3>
                <div className="text-sm text-stone-600 mt-1 flex items-center gap-1">
                  <MapPin size={14} /> {l.seller_city || "—"}
                </div>
                <div className="text-sm text-stone-700 mt-2 flex items-center gap-1">
                  <Package size={14} /> {l.quantity_kg} {t.trade.kg}
                </div>
                {l.description && (
                  <p className="text-sm text-stone-600 mt-3 line-clamp-2">{l.description}</p>
                )}
                <a
                  href={`tel:${l.seller_phone}`}
                  data-testid={`contact-seller-btn-${idx}`}
                  className="btn-primary mt-4 w-full"
                >
                  <Phone size={16} /> {t.trade.contact}
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default Trade;
